import React, { useState } from 'react';

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Table,
    TableCell,
    TableHead,
    TableRow,
    TextField as MuiTextField,
    Typography,
} from '@material-ui/core';
import i18n from 'i18n-js';

import { images } from 'assets';

import styles from '../../../styles';
import { helperFunctions } from 'helpers';
import { Text } from 'components/shared';
import { FinanceData, FinanceDishData } from 'store/financeStore/reducer';

interface FinanceItemDetailsProps {
    financeItem: FinanceData;
}

export const FinanceItemDetails = ({ financeItem }: FinanceItemDetailsProps) => {
    const [expanded, setExpanded] = useState<boolean[]>([]);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [textFilters, setTextFilters] = useState(['', '', '', '']);
    const classes = styles();

    function getTotalPriceOfEach(dishes: FinanceDishData[]) {
        return dishes
            .reduce((a: number, b: FinanceDishData) => a + b.price * b.number, 0)
            .toFixed(2);
    }

    function filterDataBySearchText(dish: {
        name: string | number;
        price: number;
        number: number;
    }) {
        let passed = true;
        for (let i = 0; i < 3; i++) {
            let val: string | number = '';
            switch (i) {
                case 0:
                    val = dish.name;
                    break;
                case 1:
                    val = dish.price;
                    break;
                case 2:
                    val = dish.number;
                    break;
                case 3:
                    val = (dish.price * dish.number).toFixed(2);
                    break;
            }
            const hasPassed = val.toString().toLowerCase().indexOf(textFilters[i]) >= 0;
            if (!hasPassed) {
                passed = false;
                break;
            }
        }
        return passed;
    }

    function setSearchTextFilter(text: string, index: number) {
        const newFilters = [...textFilters];
        newFilters[index] = text;
        setTextFilters(newFilters);
    }

    return (
        <>
            <Table
                aria-label="simple table"
                className={`${classes.noBordersHeader} ${classes.mTop10}`}
            >
                <TableHead>
                    <TableCell
                        style={{
                            width: '32%',
                        }}
                        component={'td'}
                        align={'left'}
                    >
                        <MuiTextField
                            className={
                                focusedIndex === 1 ? classes.focusedTextField : classes.textField
                            }
                            onFocus={() => setFocusedIndex(1)}
                            onBlur={() => setFocusedIndex(0)}
                            onChange={(e) => setSearchTextFilter(e.target.value, 0)}
                            placeholder={i18n.t('restaurant.product')}
                            style={{ width: '100px' }}
                            InputProps={{
                                endAdornment: (
                                    <img
                                        src={images.icons.search}
                                        className={classes.searchIcon}
                                        alt="seach-icon"
                                    />
                                ),
                                disableUnderline: true,
                            }}
                        />
                    </TableCell>
                    <TableCell style={{ width: '25%' }} component={'td'}>
                        <MuiTextField
                            className={
                                focusedIndex === 2 ? classes.focusedTextField : classes.textField
                            }
                            onFocus={() => setFocusedIndex(2)}
                            onBlur={() => setFocusedIndex(0)}
                            onChange={(e) => setSearchTextFilter(e.target.value, 1)}
                            placeholder={i18n.t('restaurant.productPrice')}
                            style={{ width: '140px' }}
                            InputProps={{
                                endAdornment: (
                                    <img
                                        src={images.icons.search}
                                        className={classes.searchIcon}
                                        alt="search-icon"
                                    />
                                ),
                                disableUnderline: true,
                            }}
                        />
                    </TableCell>
                    <TableCell style={{ width: '25%' }} component={'td'}>
                        <MuiTextField
                            className={
                                focusedIndex === 3 ? classes.focusedTextField : classes.textField
                            }
                            onFocus={() => setFocusedIndex(3)}
                            onBlur={() => setFocusedIndex(0)}
                            onChange={(e) => setSearchTextFilter(e.target.value, 2)}
                            placeholder={helperFunctions.truncateLangString(
                                i18n.t('restaurant.numberOfOrders'),
                                17
                            )}
                            style={{ width: '170px' }}
                            InputProps={{
                                endAdornment: (
                                    <img
                                        src={images.icons.search}
                                        alt="search-icon"
                                        className={classes.searchIcon}
                                    />
                                ),
                                disableUnderline: true,
                            }}
                        />
                    </TableCell>
                    <TableCell style={{ width: '18%' }} component={'td'}>
                        <MuiTextField
                            className={
                                focusedIndex === 4 ? classes.focusedTextField : classes.textField
                            }
                            onFocus={() => setFocusedIndex(4)}
                            onBlur={() => setFocusedIndex(0)}
                            onChange={(e) => setSearchTextFilter(e.target.value, 3)}
                            placeholder={i18n.t('form.cancelled')}
                            style={{ width: '130px' }}
                            InputProps={{
                                endAdornment: (
                                    <img
                                        src={images.icons.search}
                                        alt="search-icon"
                                        className={classes.searchIcon}
                                    />
                                ),
                                disableUnderline: true,
                            }}
                        />
                    </TableCell>
                    <TableCell style={{ width: '18%' }} component={'td'} align={'right'}>
                        <MuiTextField
                            className={
                                focusedIndex === 5 ? classes.focusedTextField : classes.textField
                            }
                            onFocus={() => setFocusedIndex(5)}
                            onBlur={() => setFocusedIndex(0)}
                            onChange={(e) => setSearchTextFilter(e.target.value, 4)}
                            placeholder={i18n.t('common.totalAmount')}
                            style={{ width: '130px' }}
                            InputProps={{
                                endAdornment: (
                                    <img
                                        src={images.icons.search}
                                        alt="search-icon"
                                        className={classes.searchIcon}
                                    />
                                ),
                                disableUnderline: true,
                            }}
                        />
                    </TableCell>
                </TableHead>
            </Table>
            <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 250px)' }}>
                {financeItem.services?.map(
                    (
                        service: {
                            name: string;
                            totalAmount: number;
                            dishes: FinanceDishData[];
                            cancelledDishes: FinanceDishData[];
                        },
                        i
                    ) => (
                        <Accordion
                            classes={classes}
                            className={classes.accordion}
                            onChange={(x) => {
                                const newExpanded = [...expanded];
                                newExpanded[i] = expanded[i] !== undefined ? !expanded[i] : false;
                                setExpanded(newExpanded);
                            }}
                            expanded={expanded[i] !== undefined ? expanded[i] : true}
                        >
                            <AccordionSummary
                                className={classes.accordionBGColor}
                                aria-controls="panel1d-content"
                                id="panel1d-header"
                            >
                                <Typography className={classes.centeredTypography}>
                                    {i18n.t(`form.${service.name}`)}
                                </Typography>
                                <div className={classes.priceAmountContainer}>
                                    <span className={classes.currencyText}>
                                        {i18n.t('common.currencies.chf')}
                                    </span>
                                    <span className={classes.currencyAmount}>
                                        {getTotalPriceOfEach(service.dishes)}
                                    </span>
                                </div>
                            </AccordionSummary>
                            <AccordionDetails style={{ padding: 0 }}>
                                <Table aria-label="simple table">
                                    {service.dishes.filter(filterDataBySearchText).map((dish) => (
                                        <TableRow>
                                            <TableCell
                                                style={{ width: '32%' }}
                                                component={'td'}
                                                align={'left'}
                                            >
                                                <Text
                                                    text={dish.name.trimLeft()}
                                                    fontSize={14}
                                                    maxLength={25}
                                                />
                                            </TableCell>
                                            <TableCell style={{ width: '25%' }} component={'td'}>
                                                {dish.price}
                                            </TableCell>
                                            <TableCell style={{ width: '25%' }} component={'td'}>
                                                {dish.number}
                                            </TableCell>
                                            <TableCell>
                                                {service.cancelledDishes.find(
                                                    (x) => x.name === dish.name
                                                )?.number || 0}
                                            </TableCell>
                                            <TableCell
                                                style={{ width: '18%' }}
                                                component={'td'}
                                                align={'right'}
                                            >
                                                {(dish.price * dish.number).toFixed(2)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </Table>
                            </AccordionDetails>
                        </Accordion>
                    )
                )}
            </div>
        </>
    );
};
